import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { MasterRoomAutoAddConfig, OrgRoomAutoAddConfig } from '../types/admin';

// ── Master Auto-Add Rules ─────────────────────────────────────────────────

interface MasterAutoAddRulesResponse {
  success: boolean;
  data: MasterRoomAutoAddConfig[];
}

interface BulkUploadResponse {
  success: boolean;
  data: { created: number; updated: number };
}

export function useAutoAddRules() {
  const [rules, setRules] = useState<MasterRoomAutoAddConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get<MasterAutoAddRulesResponse>('/admin/auto-add-rules');
      setRules(res.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const saveRules = useCallback(async (updated: MasterRoomAutoAddConfig[]) => {
    const res = await api.put<MasterAutoAddRulesResponse>('/admin/auto-add-rules', { rules: updated });
    setRules(res.data ?? []);
  }, []);

  const bulkUpload = useCallback(async (uploaded: unknown[]): Promise<{ created: number; updated: number }> => {
    const res = await api.post<BulkUploadResponse>('/admin/auto-add-rules/bulk-upload', { rules: uploaded });
    await fetchRules();
    return res.data;
  }, [fetchRules]);

  return { rules, loading, error, saveRules, bulkUpload, refetch: fetchRules };
}

// ── Org Auto-Add Rules ────────────────────────────────────────────────────

interface OrgAutoAddRulesResponse {
  success: boolean;
  data: OrgRoomAutoAddConfig[];
}

export function useOrgAutoAddRules(orgId: string) {
  const [rules, setRules] = useState<OrgRoomAutoAddConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRules = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get<OrgAutoAddRulesResponse>(`/org-admin/${orgId}/auto-add-rules`);
      setRules(res.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const saveRules = useCallback(async (updated: OrgRoomAutoAddConfig[]) => {
    const res = await api.put<OrgAutoAddRulesResponse>(`/org-admin/${orgId}/auto-add-rules`, { rules: updated });
    setRules(res.data ?? []);
  }, [orgId]);

  const syncFromMaster = useCallback(async (): Promise<{ added: number; updated: number }> => {
    const res = await api.post<{ success: boolean; data: { added: number; updated: number } }>(
      `/org-admin/${orgId}/auto-add-rules/sync-from-master`,
    );
    await fetchRules();
    return res.data;
  }, [orgId, fetchRules]);

  return { rules, loading, error, saveRules, syncFromMaster, refetch: fetchRules };
}
